import React, { FC, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { IconPreloader } from '@flicent/fieldpulse-react-component-library-frontend';

import { AcornApi, AcornStandardParameters } from '../../hooks/acorn/acorn-script.types';
import { useAcornCdnScript } from '../../hooks/acorn/useAcornCdnScript';
import { AcornWidgetParameters } from '../../models/acorn-widget-parameters.model';
import { ToastService } from '../../service/ToastService';
import { getInvoiceAcornPromoParametersRequest, getPublicLinkAcornPromoParametersRequest } from '../../api/AcornApi';

const ACORN_WIDGET_ID = 'acorn-fdp-estimate-react';

const AppearingKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.7);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;


const PulseKeyframes = keyframes`
  50% {
    opacity: 0.7;
  }
`;

const StyledRoot = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 10px;
  justify-items: center;
  align-items: center;
  min-height: 55px;

  animation: ${ AppearingKeyframes } 0.5s ease-in-out;
`;

interface StyledWidgetWrapperProps {
  $isPreviewMode: boolean;
}

const StyledWidgetWrapper = styled.div<StyledWidgetWrapperProps>`
  width: 100%;
  cursor: pointer;
  ${ ({ $isPreviewMode }) => $isPreviewMode && css`
    & > * {
      pointer-events: none;
    }
  ` };

  acorn-standard-widget {
    width: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const StyledBannerFetchingOverlay = styled.div`
  z-index: 2;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: grid;
  place-items: center;
  border-radius: 5px;

  background-color: rgb(111 174 255 / 21%);
  backdrop-filter: blur(3px);

  animation: ${ PulseKeyframes } 2s cubic-bezier(.4, 0, .6, 1) infinite;
  cursor: wait;
`;


interface AcornWidgetProps {
  isPreviewMode: boolean;
  loanAmount: number;
}

const AcornWidget: FC<AcornWidgetProps> = ({ isPreviewMode, loanAmount }) => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [acornWidgetParameters, setAcornWidgetParameters] = useState<AcornWidgetParameters | null>(null);
  const acornScriptApi: AcornApi | null = useAcornCdnScript(acornWidgetParameters?.cdn_url);

  const isRenderNull: boolean = !isFetching && (isError || !acornWidgetParameters);

  // Update Acorn Widget Parameters
  useEffect(() => {
    if (!acornScriptApi || !acornWidgetParameters) {
      return;
    }

    try {
      const payload: AcornStandardParameters = {
        ...acornWidgetParameters.loan_details,
        // ATTENTION: We must not specify returnURL in the preview mode to not expose the user's Authorization Token!
        returnURL: isPreviewMode ? '' : window.location.href,
        // ==========================================================================================
        showPromoText: true,
        popupType: 'continue',
        pageType: 'calculator',
        theme: 'light',
        alignment: 'center',
        acornStyle: `
          box-sizing: border-box;
          width: 100%;
          padding: 10px;
          border-radius: 6px;
        `,
        acornPaymentStyle: 'font-weight: bold;',
      };

      Object.assign(acornScriptApi.acornStandardParameters, payload);

      acornScriptApi.setAcornStandardParameters(ACORN_WIDGET_ID);
    } catch (e) {
      console.error(e);
    }
  }, [isPreviewMode, acornScriptApi, acornWidgetParameters]);

  // Fetch Acorn Promo Parameters
  useEffect(() => {
    // If there is no loan amount, we should not show the widget
    if (!loanAmount) {
      return;
    }

    const controller = new AbortController();

    setIsError(false);
    setIsFetching(true);

    let requestWasCanceled: boolean = false;

    const getLoanAmountFunction = isPreviewMode ? getInvoiceAcornPromoParametersRequest : getPublicLinkAcornPromoParametersRequest;

    getLoanAmountFunction(loanAmount, controller.signal)
      .then(setAcornWidgetParameters)
      .catch((error: Error) => {
        // We ignore the cancel error, because it's a normal situation
        // We pass `signal` to the request function to cancel it when the loan amount changes
        if (error.name === 'AbortError') {
          requestWasCanceled = true;
          return;
        }

        setIsError(true);
        console.error(error);
      })
      .finally(() => {
        if (!requestWasCanceled) {
          setIsFetching(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [isPreviewMode, loanAmount]);

  if (isRenderNull) {
    return null;
  }

  const handleClickOnWidgetWrapper = (e: React.MouseEvent) => {
    if (isPreviewMode) {
      e.preventDefault();
      e.stopPropagation();
      ToastService.middleScreenWarningToast('This action is not available in preview mode.');
    }
  };

  return (
    <StyledRoot onClick={ handleClickOnWidgetWrapper }>
      {
        isFetching && (
          <StyledBannerFetchingOverlay>
            <IconPreloader/>
          </StyledBannerFetchingOverlay>
        )
      }

      <StyledWidgetWrapper
        $isPreviewMode={ isPreviewMode }
        dangerouslySetInnerHTML={ {
          __html: `<acorn-standard-widget id="${ACORN_WIDGET_ID}"  />`
        } }
      />
    </StyledRoot>
  );
};

export default AcornWidget;
